import { useEffect, useRef, type RefObject } from 'react';

export function useDialogFocus(open: boolean, ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open || !ref.current) return;
    const previous = document.activeElement as HTMLElement | null;
    const scrollY = window.scrollY;
    const body = document.body;
    body.classList.add('modal-open');
    body.style.top = `-${scrollY}px`;
    const focusable = () => [
      ...ref.current!.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    const app = document.querySelector('main');
    const previousInert = app?.inert ?? false;
    if (app) app.inert = true;
    (focusable()[0] ?? ref.current)?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      // Safari's native Tab order can omit links. Advance explicitly so every
      // control remains reachable and focus stays inside the dialog.
      event.preventDefault();
      const current = items.indexOf(document.activeElement as HTMLElement);
      const next =
        current < 0
          ? event.shiftKey
            ? items.length - 1
            : 0
          : (current + (event.shiftKey ? -1 : 1) + items.length) % items.length;
      items[next].focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      body.classList.remove('modal-open');
      body.style.top = '';
      if (app) app.inert = previousInert;
      window.scrollTo(0, scrollY);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open, ref]);
}
